// stores/index.js - Stores otimizadas com WebSocket

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { tunnelAPI, userAPI } from '@/services/api' // Adicionado userAPI

// ==========================================
// UI Store (mantida igual)
// ==========================================
export const useUIStore = defineStore('ui', () => {
  const isLoading = ref(false)
  const loadingMessage = ref('')
  const notifications = ref([])
  const sidebarOpen = ref(false)
  
  function setLoading(loading, message = '') {
    isLoading.value = loading
    loadingMessage.value = message
  }
  
  function addNotification(notification) {
    const id = Date.now()
    notifications.value.push({
      id,
      ...notification,
      timestamp: new Date()
    })
    
    setTimeout(() => {
      removeNotification(id)
    }, 5000)
    
    return id
  }
  
  function removeNotification(id) {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }
  
  function toggleSidebar() {
    sidebarOpen.value = !sidebarOpen.value
  }
  
  return {
    isLoading,
    loadingMessage,
    notifications,
    sidebarOpen,
    setLoading,
    addNotification,
    removeNotification,
    toggleSidebar
  }
})

// ==========================================
// WebSocket Store - Centralizada
// ==========================================
export const useWebSocketStore = defineStore('websocket', () => {
  const ws = ref(null)
  const isConnected = ref(false)
  const reconnectAttempts = ref(0)
  const maxReconnectAttempts = 5
  const reconnectInterval = ref(null)
  
  const messageHandlers = ref(new Map())
  
  function connect() {
    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const wsUrl = `${protocol}//${window.location.host}/ws/tunnels/`
      
      ws.value = new WebSocket(wsUrl)
      
      ws.value.onopen = () => {
        console.log('✅ WebSocket conectado')
        isConnected.value = true
        reconnectAttempts.value = 0
        
        if (reconnectInterval.value) {
          clearInterval(reconnectInterval.value)
          reconnectInterval.value = null
        }
        
        sendMessage({ type: 'request_tunnels' })
        sendMessage({ type: 'request_users' })
        sendMessage({ type: 'request_logs' })
      }
      
      ws.value.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          handleMessage(data)
        } catch (error) {
          console.error('Erro ao processar mensagem WebSocket:', error)
        }
      }
      
      ws.value.onclose = (event) => {
        console.log('🔌 WebSocket desconectado:', event.code)
        isConnected.value = false
        
        if (event.code !== 1000 && reconnectAttempts.value < maxReconnectAttempts) {
          scheduleReconnect()
        }
      }
      
      ws.value.onerror = (error) => {
        console.error('❌ Erro no WebSocket:', error)
        isConnected.value = false
      }
      
    } catch (error) {
      console.error('Erro ao conectar WebSocket:', error)
      scheduleReconnect()
    }
  }
  
  function disconnect() {
    if (ws.value) {
      ws.value.close(1000)
      ws.value = null
    }
    
    if (reconnectInterval.value) {
      clearInterval(reconnectInterval.value)
      reconnectInterval.value = null
    }
    
    isConnected.value = false
    reconnectAttempts.value = 0
  }
  
  function scheduleReconnect() {
    if (reconnectAttempts.value >= maxReconnectAttempts) {
      console.error('🚫 Máximo de tentativas de reconexão atingido')
      return
    }
    
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.value), 30000)
    
    console.log(`🔄 Tentando reconectar em ${delay}ms (tentativa ${reconnectAttempts.value + 1})`)
    
    reconnectInterval.value = setTimeout(() => {
      reconnectAttempts.value++
      connect()
    }, delay)
  }
  
  function sendMessage(message) {
    // Conferir o readyState do socket, nao a nossa flag: o onopen ja chamava
    // sendMessage enquanto o socket ainda estava em CONNECTING, e o send()
    // lancava InvalidStateError -- que derrubava o resto do onopen junto.
    if (ws.value && ws.value.readyState === WebSocket.OPEN) {
      ws.value.send(JSON.stringify(message))
    } else {
      console.warn('WebSocket não conectado, mensagem não enviada:', message)
    }
  }
  
  function handleMessage(data) {
    const handler = messageHandlers.value.get(data.type)
    if (handler) {
      handler(data)
    } else {
      console.log('Mensagem WebSocket não tratada:', data.type)
    }
  }
  
  function onMessage(type, handler) {
    messageHandlers.value.set(type, handler)
  }
  
  function offMessage(type) {
    messageHandlers.value.delete(type)
  }
  
  return {
    isConnected,
    reconnectAttempts,
    connect,
    disconnect,
    sendMessage,
    onMessage,
    offMessage
  }
})

// ==========================================
// Users Store (atualizada para usar userAPI)
// ==========================================
export const useUserStore = defineStore('users', () => {
  const users = ref([])
  const isLoading = ref(false)
  const error = ref('')
  
  const wsStore = useWebSocketStore()
  
  const userStats = computed(() => {
    const totalUsers = users.value.length
    const activeUsers = users.value.filter(u => u.is_active).length
    const inactiveUsers = users.value.filter(u => !u.is_active).length
    const adminUsers = users.value.filter(u => u.user_type === 'admin').length
    const normalUsers = users.value.filter(u => u.user_type === 'normal' || !u.user_type).length
    
    return {
      total_users: totalUsers,
      active_users: activeUsers,
      inactive_users: inactiveUsers,
      admin_users: adminUsers,
      normal_users: normalUsers
    }
  })
  
  function setupWebSocketHandlers() {
    wsStore.onMessage('users_data', (data) => {
      users.value = data.users || []
    })
    
    wsStore.onMessage('user_created', (data) => {
      users.value.unshift(data.user)
    })
    
    wsStore.onMessage('user_updated', (data) => {
      updateUserInList(data.user)
    })
    
    wsStore.onMessage('user_deleted', (data) => {
      removeUserFromList(data.user_id)
    })
  }
  
  function updateUserInList(updatedUser) {
    const index = users.value.findIndex(u => u.id === updatedUser.id)
    if (index !== -1) {
      users.value[index] = { ...users.value[index], ...updatedUser }
    }
  }
  
  function removeUserFromList(userId) {
    const index = users.value.findIndex(u => u.id === userId)
    if (index !== -1) {
      users.value.splice(index, 1)
    }
  }
  
  // API Actions (atualizadas para usar userAPI)
  async function fetchUsers() {
    try {
      isLoading.value = true
      error.value = ''
      console.log('📡 Buscando usuários via API')
      
      const response = await userAPI.list()
      console.log('Resposta da API de usuários:', response)
      
      let userData = []
      if (response.data) {
        if (response.data.results && Array.isArray(response.data.results)) {
          userData = response.data.results
        } else if (Array.isArray(response.data)) {
          userData = response.data
        }
      }
      
      users.value = userData
      console.log(`✅ ${userData.length} usuários carregados`)
      
      return { success: true, users: userData }
    } catch (error) {
      console.error('❌ Erro ao carregar usuários:', error)
      error.value = error.response?.data?.message || error.message || 'Erro ao carregar usuários'
      return { success: false, message: error.value }
    } finally {
      isLoading.value = false
    }
  }
  
  async function createUser(userData) {
    try {
      console.log('📝 Criando usuário:', userData)
      
      if (userData.password !== userData.password_confirm) {
        throw new Error('As senhas não coincidem')
      }
      
      if (userData.password.length < 8) {
        throw new Error('A senha deve ter pelo menos 8 caracteres')
      }
      
      if (!userData.username?.trim()) {
        throw new Error('Username é obrigatório')
      }
      
      const response = await userAPI.create({
        first_name: userData.first_name,
        last_name: userData.last_name,
        username: userData.username,
        email: userData.email,
        password: userData.password,
        password_confirm: userData.password_confirm
      })
      
      console.log('✅ Usuário criado:', response.data)
      
      if (response.data) {
        users.value.unshift(response.data)
      }
      
      return { success: true, user: response.data }
    } catch (error) {
      console.error('❌ Erro ao criar usuário:', error)
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     error.message ||
                     'Erro ao criar usuário'
      return { success: false, message }
    }
  }
  
  async function updateUser(userId, userData) {
    try {
      console.log(`📝 Atualizando usuário ${userId}:`, userData)
      
      const response = await userAPI.update(userId, userData)
      
      const index = users.value.findIndex(u => u.id === userId)
      if (index !== -1) {
        users.value[index] = { ...users.value[index], ...response.data }
      }
      
      console.log('✅ Usuário atualizado com sucesso')
      return { success: true, user: response.data }
    } catch (error) {
      console.error('❌ Erro ao atualizar usuário:', error)
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     error.message ||
                     'Erro ao atualizar usuário'
      return { success: false, message }
    }
  }
  
  async function toggleUserStatus(userId) {
    try {
      const user = users.value.find(u => u.id === userId)
      if (!user) {
        throw new Error('Usuário não encontrado')
      }
      
      console.log(`🔄 Alterando status do usuário ${userId}`)
      
      const response = await userAPI.update(userId, {
        is_active: !user.is_active
      })
      
      user.is_active = !user.is_active
      
      console.log('✅ Status alterado com sucesso')
      return { success: true, user: response.data }
    } catch (error) {
      console.error('❌ Erro ao alterar status do usuário:', error)
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     error.message ||
                     'Erro ao alterar status do usuário'
      return { success: false, message }
    }
  }
  
  async function deleteUser(userId) {
    try {
      console.log(`🗑️ Excluindo usuário ${userId}`)
      
      await userAPI.delete(userId)
      
      users.value = users.value.filter(u => u.id !== userId)
      
      console.log('✅ Usuário excluído com sucesso')
      return { success: true }
    } catch (error) {
      console.error('❌ Erro ao excluir usuário:', error)
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     error.message ||
                     'Erro ao excluir usuário'
      return { success: false, message }
    }
  }
  
  function requestUsersUpdate() {
    if (wsStore.isConnected) {
      wsStore.sendMessage({ type: 'request_users' })
    }
  }
  
  function initializeWebSocket() {
    setupWebSocketHandlers()
  }
  
  function getUserDisplayName(user) {
    if (user.display_name) return user.display_name
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`.trim()
    }
    if (user.first_name) return user.first_name
    if (user.last_name) return user.last_name
    if (user.username) return user.username
    if (user.email) return user.email.split('@')[0]
    return 'Nome não informado'
  }
  
  function getUserTypeLabel(userType) {
    switch (userType) {
      case 'admin':
        return 'Admin'
      case 'supervisor':
        return 'Supervisor'
      case 'normal':
        return 'Usuário'
      default:
        return 'Usuário'
    }
  }
  
  function getUserInitials(user) {
    if (user.first_name && user.last_name) {
      return (user.first_name[0] + user.last_name[0]).toUpperCase()
    }
    if (user.first_name) {
      return user.first_name[0].toUpperCase()
    }
    if (user.username) {
      return user.username.substring(0, 2).toUpperCase()
    }
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase()
    }
    return '??'
  }
  
  function formatDate(dateString) {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }
  
  return {
    users,
    isLoading,
    error,
    userStats,
    fetchUsers,
    createUser,
    updateUser,
    toggleUserStatus,
    deleteUser,
    requestUsersUpdate,
    initializeWebSocket,
    getUserDisplayName,
    getUserTypeLabel,
    getUserInitials,
    formatDate
  }
})

// ==========================================
// Tunnels Store - COM WEBSOCKET
// ==========================================
// ==========================================
// Tunnels Store - COM PREVENÇÃO DE DUPLICAÇÃO
// ==========================================

export const useTunnelStore = defineStore('tunnels', () => {
  const tunnels = ref([])
  const stats = ref({
    total_tunnels: 0,
    running_tunnels: 0,
    stopped_tunnels: 0,
    error_tunnels: 0,
    total_traffic: 0,
    total_connections: 0,
    avg_traffic_per_tunnel: 0
  })
  const isLoading = ref(false)
  const isCreating = ref(false)
  const isUpdating = ref(false)
  const isDeleting = ref(false)
  
  // IDs de túneis sendo processados (para evitar duplicação)
  const processingTunnels = ref(new Set())
  
  const wsStore = useWebSocketStore()
  
  const realTimeStats = computed(() => {
    const totalTunnels = tunnels.value.length
    const runningTunnels = tunnels.value.filter(t => t.status === 'running').length
    const stoppedTunnels = tunnels.value.filter(t => t.status === 'stopped').length
    const errorTunnels = tunnels.value.filter(t => t.status === 'error').length
    
    const totalTraffic = tunnels.value.reduce((sum, t) => sum + (t.total_traffic || 0), 0)
    const totalConnections = tunnels.value.reduce((sum, t) => sum + (t.total_connections || 0), 0)
    const avgTrafficPerTunnel = totalTunnels > 0 ? totalTraffic / totalTunnels : 0
    
    return {
      total_tunnels: totalTunnels,
      running_tunnels: runningTunnels,
      stopped_tunnels: stoppedTunnels,
      error_tunnels: errorTunnels,
      total_traffic: totalTraffic,
      total_connections: totalConnections,
      avg_traffic_per_tunnel: avgTrafficPerTunnel
    }
  })
  
  function setupWebSocketHandlers() {
    // O backend manda os tuneis em DUAS mensagens: `initial_data` (logo que
    // conecta) e `tunnels_data` (quando pedimos). Só a segunda era tratada,
    // então a primeira caía no "Mensagem WebSocket não tratada" e a tela
    // ficava esperando a rodada seguinte.
    const aplicarTuneis = (data) => {
      console.log('📡 Dados de túneis recebidos via WebSocket:', data.tunnels?.length || 0)

      // Substituir completamente os dados para evitar duplicação
      tunnels.value = data.tunnels || []

      // Limpar túneis em processamento
      processingTunnels.value.clear()
    }

    wsStore.onMessage('tunnels_data', aplicarTuneis)
    wsStore.onMessage('initial_data', aplicarTuneis)

    // As estatisticas sao calculadas aqui a partir dos tuneis (computed
    // `stats`), entao o `stats_update` do backend nao acrescenta nada --
    // registramos so para nao poluir o console de "não tratada" a cada ciclo.
    wsStore.onMessage('stats_update', () => {})


    wsStore.onMessage('tunnel_update', (data) => {
      updateTunnelInList(data.data)
    })
    
    wsStore.onMessage('tunnel_created', (data) => {
      console.log('✨ Novo túnel recebido via WebSocket:', data.tunnel)
      
      // Verificar se túnel já existe antes de adicionar
      if (data.tunnel && !findTunnelById(data.tunnel.id)) {
        tunnels.value.unshift(data.tunnel)
        console.log('➕ Túnel adicionado à lista')
      } else {
        console.log('⚠️ Túnel já existe, ignorando duplicação')
      }
    })
    
    wsStore.onMessage('tunnel_deleted', (data) => {
      console.log('🗑️ Túnel removido via WebSocket:', data.tunnel_id)
      removeTunnelFromList(data.tunnel_id)
    })
    
    wsStore.onMessage('connection_established', (data) => {
      console.log('🔌 WebSocket conectado')
    })
    
    wsStore.onMessage('pong', (data) => {
      // Heartbeat silencioso
    })
  }
  
  function findTunnelById(id) {
    return tunnels.value.find(t => t.id === id || t.id === String(id))
  }
  
  function updateTunnelInList(update) {
    const tunnel = findTunnelById(update.tunnel_id)
    if (tunnel) {
      // Atualizar propriedades específicas sem sobrescrever o objeto inteiro
      Object.assign(tunnel, {
        status: update.status,
        connection_status: update.connection_status,
        // is_running decide o botao Iniciar/Parar. Quando nao vem no
        // broadcast, deduz do status -- senao o botao fica dizendo "Parar"
        // num tunel ja parado.
        is_running: update.is_running !== undefined
          ? update.is_running
          : ['running', 'starting', 'reconnecting'].includes(update.status),
        bytes_sent: update.bytes_sent || tunnel.bytes_sent,
        bytes_received: update.bytes_received || tunnel.bytes_received,
        color_status: update.color_status,
        total_traffic: (update.bytes_sent || 0) + (update.bytes_received || 0),
        last_activity: new Date().toISOString()
      })
      
      console.log(`📈 Túnel ${update.name || tunnel.name} atualizado:`, {
        status: update.status,
        traffic: `${update.bytes_sent || 0}↑ ${update.bytes_received || 0}↓`
      })
    }
  }
  
  function removeTunnelFromList(tunnelId) {
    const index = tunnels.value.findIndex(t => t.id === tunnelId || t.id === String(tunnelId))
    if (index !== -1) {
      const removedTunnel = tunnels.value.splice(index, 1)[0]
      console.log(`🗑️ Túnel ${removedTunnel.name} removido da lista`)
    }
    
    // Remover da lista de processamento também
    processingTunnels.value.delete(tunnelId)
    processingTunnels.value.delete(String(tunnelId))
  }
  
  function initializeWebSocket() {
    setupWebSocketHandlers()
    wsStore.connect()
  }
  
  async function fetchTunnels() {
    if (isLoading.value) {
      console.log('⏳ Já carregando túneis, ignorando nova requisição')
      return
    }
    
    try {
      isLoading.value = true
      console.log('📡 Buscando túneis via API (fallback)')
      
      const response = await tunnelAPI.list()
      const tunnelData = response.data.results || response.data || []
      
      // Verificar se os dados são válidos
      if (Array.isArray(tunnelData)) {
        tunnels.value = tunnelData
        console.log(`✅ ${tunnelData.length} túneis carregados via API`)
      } else {
        console.error('❌ Dados de túneis inválidos recebidos da API:', response.data)
        tunnels.value = []
      }
      
    } catch (error) {
      console.error('❌ Erro ao buscar túneis:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }
  
  async function createTunnel(tunnelData) {
    // Verificar se já está criando um túnel com os mesmos dados
    const tunnelKey = `${tunnelData.name}_${tunnelData.local_port}_${tunnelData.remote_port}`
    if (processingTunnels.value.has(tunnelKey)) {
      console.log('⚠️ Túnel já está sendo criado, ignorando duplicação')
      return { success: false, message: 'Túnel já está sendo criado' }
    }
    
    if (isCreating.value) {
      console.log('⚠️ Já criando um túnel, aguarde')
      return { success: false, message: 'Aguarde a criação do túnel anterior' }
    }
    
    try {
      isCreating.value = true
      processingTunnels.value.add(tunnelKey)
      
      console.log('📝 Criando túnel:', tunnelData)
      
      const response = await tunnelAPI.create(tunnelData)
      
      if (response.data) {
        console.log('✅ Túnel criado com sucesso:', response.data)
        
        // Verificar se o túnel já existe na lista antes de adicionar
        if (!findTunnelById(response.data.id)) {
          tunnels.value.unshift(response.data)
          console.log('➕ Túnel adicionado à lista local')
        } else {
          console.log('⚠️ Túnel já existe na lista, não adicionando duplicata')
        }
        
        return { success: true, tunnel: response.data }
      } else {
        throw new Error('Resposta inválida do servidor')
      }
      
    } catch (error) {
      console.error('❌ Erro ao criar túnel:', error)
      
      let message = 'Erro ao criar túnel'
      if (error.response?.data) {
        const errorData = error.response.data
        
        // Tratar erros de validação de porta
        if (errorData.local_port) {
          message = errorData.local_port[0] || errorData.local_port
        } else if (errorData.remote_port) {
          message = errorData.remote_port[0] || errorData.remote_port
        } else if (errorData.name) {
          message = errorData.name[0] || errorData.name
        } else if (errorData.message) {
          message = errorData.message
        } else if (errorData.error) {
          message = errorData.error
        } else if (typeof errorData === 'string') {
          message = errorData
        }
      }
      
      return { success: false, message }
      
    } finally {
      isCreating.value = false
      processingTunnels.value.delete(tunnelKey)
    }
  }
  
  async function updateTunnel(id, tunnelData) {
    if (processingTunnels.value.has(id)) {
      return { success: false, message: 'Túnel já está sendo atualizado' }
    }
    
    try {
      isUpdating.value = true
      processingTunnels.value.add(id)
      
      console.log(`📝 Atualizando túnel ${id}:`, tunnelData)
      
      const response = await tunnelAPI.update(id, tunnelData)
      
      if (response.data) {
        // Atualizar túnel na lista
        const index = tunnels.value.findIndex(t => t.id === id || t.id === String(id))
        if (index !== -1) {
          tunnels.value[index] = response.data
          console.log('✅ Túnel atualizado na lista local')
        }
        
        return { success: true, tunnel: response.data }
      } else {
        throw new Error('Resposta inválida do servidor')
      }
      
    } catch (error) {
      console.error('❌ Erro ao atualizar túnel:', error)
      
      let message = 'Erro ao atualizar túnel'
      if (error.response?.data) {
        const errorData = error.response.data
        if (errorData.message) message = errorData.message
        else if (errorData.error) message = errorData.error
      }
      
      return { success: false, message }
      
    } finally {
      isUpdating.value = false
      processingTunnels.value.delete(id)
    }
  }
  
  async function deleteTunnel(id) {
    if (processingTunnels.value.has(id)) {
      return { success: false, message: 'Túnel já está sendo removido' }
    }
    
    try {
      isDeleting.value = true
      processingTunnels.value.add(id)
      
      console.log(`🗑️ Removendo túnel ${id}`)
      
      await tunnelAPI.delete(id)
      
      // Remover da lista local
      removeTunnelFromList(id)
      
      console.log('✅ Túnel removido com sucesso')
      return { success: true }
      
    } catch (error) {
      console.error('❌ Erro ao remover túnel:', error)
      
      let message = 'Erro ao remover túnel'
      if (error.response?.data?.message) {
        message = error.response.data.message
      }
      
      return { success: false, message }
      
    } finally {
      isDeleting.value = false
      processingTunnels.value.delete(id)
    }
  }
  
  async function startTunnel(id) {
    if (processingTunnels.value.has(`start_${id}`)) {
      return { success: false, message: 'Túnel já está sendo iniciado' }
    }
    
    try {
      processingTunnels.value.add(`start_${id}`)
      
      console.log(`▶️ Iniciando túnel ${id}`)
      
      const response = await tunnelAPI.start(id)
      
      // Atualizar status local imediatamente para feedback visual.
      // Usa o que o backend devolveu; `is_running` e o que o botao le.
      const tunnel = findTunnelById(id)
      if (tunnel) {
        tunnel.status = response.data?.status || 'starting'
        tunnel.is_running = response.data?.is_running ?? true
        if (response.data?.connection_status) {
          tunnel.connection_status = response.data.connection_status
        }
      }
      
      // Solicitar atualização completa via WebSocket
      setTimeout(() => {
        if (wsStore.isConnected) {
          wsStore.sendMessage({ type: 'request_tunnels' })
        }
      }, 500)
      
      console.log('✅ Túnel iniciado com sucesso')
      return { success: true, data: response.data }
      
    } catch (error) {
      console.error('❌ Erro ao iniciar túnel:', error)
      
      let message = 'Erro ao iniciar túnel'
      if (error.response?.data?.error) {
        message = error.response.data.error
      } else if (error.response?.data?.message) {
        message = error.response.data.message
      }
      
      return { success: false, message }
      
    } finally {
      processingTunnels.value.delete(`start_${id}`)
    }
  }
  
  async function stopTunnel(id) {
    if (processingTunnels.value.has(`stop_${id}`)) {
      return { success: false, message: 'Túnel já está sendo parado' }
    }
    
    try {
      processingTunnels.value.add(`stop_${id}`)
      
      console.log(`⏹️ Parando túnel ${id}`)
      
      const response = await tunnelAPI.stop(id)

      // Usar o status QUE O BACKEND DEVOLVEU. Antes escrevíamos 'stopping'
      // por cima, depois da resposta -- mas a chamada só volta quando o
      // túnel JÁ parou ('stopped'). O card ficava preso em "Parando" até
      // alguém recarregar a página.
      const tunnel = findTunnelById(id)
      if (tunnel) {
        tunnel.status = response.data?.status || 'stopped'
        // is_running e o que o botao le; sem atualizar aqui o modal
        // continuava mostrando "Parar" ate a proxima varredura
        tunnel.is_running = response.data?.is_running ?? false
        tunnel.connection_status = response.data?.connection_status || 'disconnected'
      }

      console.log('✅ Túnel parado com sucesso')
      return { success: true, data: response.data }
      
    } catch (error) {
      console.error('❌ Erro ao parar túnel:', error)
      
      let message = 'Erro ao parar túnel'
      if (error.response?.data?.error) {
        message = error.response.data.error
      } else if (error.response?.data?.message) {
        message = error.response.data.message
      }
      
      return { success: false, message }
      
    } finally {
      processingTunnels.value.delete(`stop_${id}`)
    }
  }
  
  async function restartTunnel(id) {
    if (processingTunnels.value.has(`restart_${id}`)) {
      return { success: false, message: 'Túnel já está sendo reiniciado' }
    }
    
    try {
      processingTunnels.value.add(`restart_${id}`)
      
      console.log(`🔄 Reiniciando túnel ${id}`)
      
      const response = await tunnelAPI.restart(id)
      
      // Atualizar status local
      const tunnel = findTunnelById(id)
      if (tunnel) {
        tunnel.status = 'starting'
      }
      
      console.log('✅ Túnel reiniciado com sucesso')
      return { success: true, data: response.data }
      
    } catch (error) {
      console.error('❌ Erro ao reiniciar túnel:', error)
      
      let message = 'Erro ao reiniciar túnel'
      if (error.response?.data?.error) {
        message = error.response.data.error
      } else if (error.response?.data?.message) {
        message = error.response.data.message
      }
      
      return { success: false, message }
      
    } finally {
      processingTunnels.value.delete(`restart_${id}`)
    }
  }
  
  async function checkPortAvailability(port, excludeTunnelId = null) {
    try {
      const { tunnelAPI } = await import('@/services/api')
      const response = await tunnelAPI.checkPortAvailability(port, excludeTunnelId)
      return response.data
    } catch (error) {
      console.error('❌ Erro ao verificar disponibilidade da porta:', error)
      return { available: false, message: 'Erro ao verificar porta' }
    }
  }
  
  async function getAvailablePorts(startPort = null, endPort = null, limit = 100) {
    try {
      const { tunnelAPI } = await import('@/services/api')
      const response = await tunnelAPI.getAvailablePorts(startPort, endPort, limit)
      return response.data
    } catch (error) {
      console.error('❌ Erro ao obter portas disponíveis:', error)
      return { ports: [] }
    }
  }
  
  function requestTunnelsUpdate() {
    if (wsStore.isConnected) {
      console.log('📡 Solicitando atualização de túneis via WebSocket')
      wsStore.sendMessage({ type: 'request_tunnels' })
    } else {
      console.log('📡 WebSocket desconectado, buscando via API')
      fetchTunnels()
    }
  }
  
  function cleanup() {
    console.log('🧹 Limpando store de túneis')
    processingTunnels.value.clear()
    // Não desconectar WebSocket aqui pois pode ser usado em outras páginas
  }
  
  // Função para detectar e corrigir duplicações
  function removeDuplicates() {
    const seen = new Set()
    const uniqueTunnels = []
    
    for (const tunnel of tunnels.value) {
      const key = `${tunnel.id}_${tunnel.name}_${tunnel.local_port}_${tunnel.remote_port}`
      if (!seen.has(key)) {
        seen.add(key)
        uniqueTunnels.push(tunnel)
      } else {
        console.log('🧹 Removendo túnel duplicado:', tunnel.name)
      }
    }
    
    if (uniqueTunnels.length !== tunnels.value.length) {
      tunnels.value = uniqueTunnels
      console.log(`🧹 ${tunnels.value.length - uniqueTunnels.length} duplicatas removidas`)
    }
  }
  
  return {
    tunnels,
    stats: realTimeStats,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    processingTunnels: computed(() => processingTunnels.value),
    initializeWebSocket,
    fetchTunnels,
    createTunnel,
    updateTunnel,
    deleteTunnel,
    startTunnel,
    stopTunnel,
    restartTunnel,
    checkPortAvailability,
    getAvailablePorts,
    requestTunnelsUpdate,
    cleanup,
    removeDuplicates,
    findTunnelById
  }
})

// ==========================================
// Logs Store - COM WEBSOCKET
// ==========================================
export const useLogStore = defineStore('logs', () => {
  const logs = ref([])
  const stats = ref({})
  const isLoading = ref(false)
  const filters = ref({
    level: 'all',
    event_type: 'all',
    search: ''
  })
  
  const wsStore = useWebSocketStore()
  
  function setupWebSocketHandlers() {
    wsStore.onMessage('recent_logs', (data) => {
      console.log('📜 Logs recentes recebidos')
      logs.value = data.logs || []
    })
    
    wsStore.onMessage('new_log', (data) => {
      console.log('📝 Novo log recebido')
      logs.value.unshift(data.log)
      if (logs.value.length > 100) {
        logs.value = logs.value.slice(0, 100)
      }
    })
  }
  
  function initializeWebSocket() {
    setupWebSocketHandlers()
  }
  
  async function fetchLogs(params = {}) {
    try {
      isLoading.value = true
      const { logAPI } = await import('@/services/api')
      const response = await logAPI.list({ ...filters.value, ...params })
      logs.value = response.data.results || response.data
    } catch (error) {
      console.error('Erro ao buscar logs:', error)
    } finally {
      isLoading.value = false
    }
  }
  
  function setFilters(newFilters) {
    filters.value = { ...filters.value, ...newFilters }
    fetchLogs()
  }
  
  function requestLogsUpdate() {
    if (wsStore.isConnected) {
      wsStore.sendMessage({ type: 'request_logs' })
    }
  }
  
  return {
    logs,
    stats,
    isLoading,
    filters,
    initializeWebSocket,
    fetchLogs,
    setFilters,
    requestLogsUpdate
  }
})